import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createEvent, createEventInline, createEventInlineOverride, getAccessibleChats } from '../api/eventsApi';
import { updateMyTemplateDefaultChat } from '../api/templatesApi';
import { assignRole } from '../api/rolesApi';
import { getOrgSettings } from '../api/tournamentsApi';
import { useToast } from '../components/Toast';
import EventForm from '../components/EventForm';
import RolesPanel from '../components/RolesPanel';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingSaveOnly, setLoadingSaveOnly] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);

  // Pending roles to assign after creation
  const [pendingRoles, setPendingRoles] = useState([]);

  // For "save first then show invite" flow
  const [saveFirstPending, setSaveFirstPending] = useState(false);
  const formValuesRef = useRef(null);

  // Список чатов доступных для публикации и выбранный чат
  // null = личный чат с пользователем (поведение по умолчанию)
  const [accessibleChats, setAccessibleChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);

  const [mode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode');
  });

  // true пока идёт загрузка списка чатов — форму нельзя отправить
  const [chatsLoading, setChatsLoading] = useState(mode !== 'inline');

  const [userId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('userId');
  });

  const [hash] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('hash');
  });

  const [organizationId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('organizationId');
  });

  // Связка «Событие + Турнир» из списка событий (EventsListPage): событие сохраняется
  // ТОЛЬКО как черновик (без публикации), а после сохранения идём не на страницу
  // события, а сразу на настройку турнира (CreateTournamentPage сам подхватит eventId).
  // Турнир не может существовать без события (event_id nullable=false на бэке) —
  // поэтому это всегда двухшаговый переход, а не отдельная форма.
  const [forTournament] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('forTournament') === '1';
  });

  // Пользователь в inline-режиме нажал "Выбрать чат" — переключаемся на ручной выбор.
  // Чаты загружаются лениво при первом переключении.
  const [inlineModeOverridden, setInlineModeOverridden] = useState(false);
  const chatsLoadedRef = useRef(false);

  const handleOverrideInlineMode = useCallback(() => {
    if (!chatsLoadedRef.current) {
      chatsLoadedRef.current = true;
      setChatsLoading(true);
      getAccessibleChats(userId, hash)
        .then(response => setAccessibleChats(response.chats || []))
        .catch(err => console.warn('Failed to load accessible chats:', err))
        .finally(() => setChatsLoading(false));
    }
    setInlineModeOverridden(true);
  }, [userId, hash]);

  // Не сбрасываем selectedChatId — запоминаем выбор для следующего переключения
  const handleRestoreInlineMode = useCallback(() => {
    setInlineModeOverridden(false);
  }, []);

  // Загружаем доступные чаты и defaultChatId из шаблона одним запросом.
  // В inline-режиме чаты не нужны сразу (грузятся лениво при переключении),
  // но defaultChatId всё равно нужен — передаём userId+hash для аутентификации.
  useEffect(() => {
    if (mode === 'inline') {
      // Inline: грузим только для получения defaultChatId (чаты подгрузятся в handleOverrideInlineMode)
      getAccessibleChats(userId, hash)
        .then(response => {
          if (response.defaultChatId != null) {
            setSelectedChatId(response.defaultChatId);
          }
        })
        .catch(err => console.warn('Failed to load template defaults:', err));
      return;
    }
    // Обычный режим: грузим чаты и defaultChatId вместе
    getAccessibleChats()
      .then(response => {
        setAccessibleChats(response.chats || []);
        if (response.defaultChatId != null) {
          setSelectedChatId(response.defaultChatId);
        }
      })
      .catch(err => console.warn('Failed to load accessible chats:', err))
      .finally(() => setChatsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Смена чата: обновляем локальное состояние и сохраняем предпочтение на бэкенд.
  const handleChatChange = useCallback((chatId) => {
    setSelectedChatId(chatId);
    updateMyTemplateDefaultChat(
      chatId,
      mode === 'inline' ? userId : null,
      mode === 'inline' ? hash : null,
    ).catch(err => console.warn('Failed to save default chat:', err));
  }, [mode, userId, hash]);

  // Core event creation logic — returns created event or throws.
  // publishDirectly: true = создать и опубликовать, false = только сохранить
  const doCreateEvent = async (values, publishDirectly = true) => {
    if (mode === 'inline' && !inlineModeOverridden) {
      // Чистый inline: бэкенд только создаёт событие (не публикует),
      // фронт вернёт publicId в чат через switchInlineQuery
      if (!userId || !hash) throw new Error('Missing userId or hash for inline mode');
      return await createEventInline(values, userId, hash);
    } else if (mode === 'inline' && inlineModeOverridden) {
      // Inline-override: X-Telegram-Init-Data по-прежнему недоступен,
      // аутентификация через userId+hash, но бэкенд публикует сообщение сам (publishDirectly=true)
      if (!userId || !hash) throw new Error('Missing userId or hash for inline override mode');
      return await createEventInlineOverride({ ...values, chatId: selectedChatId }, userId, hash);
    } else {
      // Обычный режим: publishDirectly управляется вызывающей стороной.
      // Используем выбор пользователя из селектора (null = личный чат)
      const resolvedChatId = selectedChatId;
      return await createEvent({ ...values, chatId: resolvedChatId }, publishDirectly);
    }
  };

  // Assign pending roles after event is created
  const assignPendingRoles = async (eventId, roles) => {
    const errors = [];
    for (const r of roles) {
      try {
        await assignRole({
          targetUserId: r.userId,
          role: r.role,
          resourceType: 'EVENT',
          resourceId: eventId,
        }, userId, hash);
      } catch (err) {
        errors.push(`${r.fullName || r.telegramUserName}: ${err.message}`);
      }
    }
    if (errors.length > 0) {
      addToast(`Некоторые роли не назначены: ${errors.join(', ')}`, 'error');
    }
  };

  // Связка «Событие + Турнир»: после того как событие создано (сохранено ИЛИ
  // опубликовано — пользователь сам выбирает кнопкой), идём не на страницу события,
  // а сразу на настройку турнира. Спорт — настройка организации (не поле события),
  // подтягиваем тем же эндпоинтом, которым EventsListPage уже проверял tournamentsEnabled.
  //
  // История браузера: сначала REPLACE текущей записи (/events/create?forTournament=1)
  // на страницу события, затем PUSH страницы настройки турнира поверх неё. Без этого
  // «назад» с настройки турнира вернул бы на пустую форму создания — с риском случайно
  // насоздавать дублей события. Так «назад» ведёт на просмотр/редактирование события,
  // которое уже создано (react-router выполняет replace/push синхронно, событие
  // не рендерится — сразу видим страницу турнира).
  const redirectToTournamentSetup = async (eventPublicId) => {
    let sport;
    try {
      const settings = await getOrgSettings();
      sport = settings?.sport;
    } catch (_) {
      // не критично: CreateTournamentPage подскажет, если спорт не пришёл
    }
    navigate(`/events/${eventPublicId}`, { replace: true });
    navigate(`/tournaments/new?eventId=${eventPublicId}${sport ? `&sport=${sport}` : ''}`);
  };

  // «Сохранить» — только записываем событие в БД, переходим на страницу просмотра
  // (или на настройку турнира — см. redirectToTournamentSetup).
  const handleSubmitSaveOnly = async (values) => {
    setLoadingSaveOnly(true);
    try {
      const createdEvent = await doCreateEvent(values, false);
      setCreatedEventId(createdEvent.id);

      if (pendingRoles.length > 0) {
        await assignPendingRoles(createdEvent.id, pendingRoles);
      }

      if (forTournament) {
        addToast('Событие сохранено! Теперь настройте турнир.', 'success');
        await redirectToTournamentSetup(createdEvent.publicId);
        return;
      }

      addToast('Событие сохранено!', 'success');
      // replace: «назад» со страницы события должен вести туда, откуда пришли
      // (список событий), а не на пустую форму создания.
      navigate(`/events/${createdEvent.publicId}`, { replace: true });
    } catch (err) {
      addToast(`Не удалось создать событие: ${err.message}`, 'error');
    } finally {
      setLoadingSaveOnly(false);
    }
  };

  // «Сохранить и опубликовать» (или единственная кнопка в inline-режиме).
  // В связке «Событие + Турнир» — не остаёмся на месте, а идём на настройку турнира
  // (см. redirectToTournamentSetup). WebApp больше не закрываем — пользователь остаётся
  // на странице (успешный экран) и сам решает, куда пойти дальше.
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const createdEvent = await doCreateEvent(values, true);
      setCreatedEventId(createdEvent.id);

      if (pendingRoles.length > 0) {
        await assignPendingRoles(createdEvent.id, pendingRoles);
      }

      if (forTournament) {
        addToast('Событие опубликовано! Теперь настройте турнир.', 'success');
        await redirectToTournamentSetup(createdEvent.publicId);
        return;
      }

      setSubmitted(true);
      addToast('Событие успешно создано!', 'success');

      // Чистый inline-режим: возвращаем publicId в чат — пользователь выбирает,
      // куда отправить сообщение с событием. В остальных случаях бэкенд уже
      // опубликовал сообщение сам — просто остаёмся на странице.
      if (window.Telegram?.WebApp && mode === 'inline' && !inlineModeOverridden) {
        window.Telegram.WebApp.switchInlineQuery(`event_${createdEvent.publicId}`);
      }
    } catch (err) {
      addToast(`Не удалось создать событие: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // publicId события созданного через saveFirst в inline-режиме —
  // нужен чтобы показать кнопку возврата в чат после того как пользователь скопировал invite-ссылку
  const [inlinePendingPublicId, setInlinePendingPublicId] = useState(null);

  // Флаг: после saveFirst автоматически открыть InviteLinkModal в пост-сабмит RolesPanel
  const [autoOpenInviteModal, setAutoOpenInviteModal] = useState(false);

  // Предупреждение при попытке открыть список событий в inline-режиме
  const [showInlineWarning, setShowInlineWarning] = useState(false);

  // "Save first" triggered from RolesPanel invite button
  // We need the current form values — store them via ref when RolesPanel triggers save
  const handleSaveFirst = useCallback(async () => {
    const values = formValuesRef.current;
    if (!values) {
      addToast('Сначала заполните форму', 'error');
      return;
    }
    setSaveFirstPending(true);
    try {
      // saveFirst — только сохраняем (без публикации), пользователь хотел сначала скопировать ссылку
      const createdEvent = await doCreateEvent(values, false);
      setCreatedEventId(createdEvent.id);

      if (pendingRoles.length > 0) {
        await assignPendingRoles(createdEvent.id, pendingRoles);
      }

      // Don't close — stay on page and switch to "view" mode (show invite panel)
      setSubmitted(true);
      // Автоматически открыть InviteLinkModal — пользователь шёл сюда именно за ссылкой
      setAutoOpenInviteModal(true);
      addToast('Событие создано! Теперь можно пригласить по ссылке.', 'success');

      // В чистом inline-режиме publicId ещё нужно вернуть в чат через switchInlineQuery.
      // Не делаем это сразу — пользователь нажал "пригласить по ссылке", пусть сначала её скопирует.
      // Кнопку "Поделиться в чате" покажем под панелью ролей.
      if (mode === 'inline' && !inlineModeOverridden) {
        setInlinePendingPublicId(createdEvent.publicId);
      }
    } catch (err) {
      addToast(`Не удалось сохранить: ${err.message}`, 'error');
    } finally {
      setSaveFirstPending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRoles, mode, userId, hash, inlineModeOverridden]);

  return (
    <div style={{ height: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '20px 16px 24px',
        color: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => mode === 'inline' ? setShowInlineWarning(true) : navigate('/events')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '5px 12px',
              color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', backdropFilter: 'blur(4px)',
              flexShrink: 0,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            Все события
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="12" y1="14" x2="12" y2="18" />
                <line x1="10" y1="16" x2="14" y2="16" />
              </svg>
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: -0.3 }}>
                Создание события
              </h1>
              {forTournament && (
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                  Шаг 1 из 2 · дальше — настройка турнира
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 16px 32px', flex: 1, overflowY: 'auto' }}>
        {/* Form Card */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '20px 16px',
          boxShadow: '0 1px 12px rgba(0,0,0,0.07)',
          marginBottom: 16,
        }}>
          {submitted ? (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '40px 20px', gap: 14, textAlign: 'center',
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#dcfce7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div>
                <p style={{ fontWeight: 700, fontSize: 17, color: '#111827', margin: '0 0 4px' }}>
                  Событие создано!
                </p>
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                  {saveFirstPending
                    ? 'Теперь вы можете настроить роли и пригласить участников.'
                    : (mode === 'inline' && !inlineModeOverridden)
                      ? 'Возвращаемся в чат...'
                      : 'Событие опубликовано в чат.'}
                </p>
              </div>
            </div>
          ) : (
            <EventForm
              onSubmit={handleSubmit}
              // Две кнопки и вне, и внутри связки «Событие + Турнир» — разница только
              // в подписях и в том, куда ведёт каждая ПОСЛЕ сохранения (см. handleSubmit/
              // handleSubmitSaveOnly): обычно на страницу события, в связке — на настройку
              // турнира. Пользователь сам решает, публиковать сразу или сначала черновик.
              onSubmitSaveOnly={mode !== 'inline' ? handleSubmitSaveOnly : undefined}
              saveOnlyLabel={forTournament ? 'Сохранить и продолжить' : 'Сохранить'}
              loadingSaveOnly={loadingSaveOnly}
              submitLabel={forTournament ? 'Опубликовать и продолжить' : (mode === 'inline' ? 'Создать событие' : 'Сохранить и опубликовать')}
              loading={loading}
              chatsLoading={chatsLoading}
              onValuesChange={(v) => { formValuesRef.current = v; }}
              // Inline-режим: показываем специальный UI вместо обычного селектора
              isInlineMode={mode === 'inline'}
              inlineModeOverridden={inlineModeOverridden}
              onOverrideInlineMode={mode === 'inline' ? handleOverrideInlineMode : undefined}
              onRestoreInlineMode={mode === 'inline' ? handleRestoreInlineMode : undefined}
              // Селектор чата: в inline — только когда пользователь переключился;
              // в обычном режиме — всегда
              availableChats={(mode !== 'inline' || inlineModeOverridden) ? accessibleChats : []}
              selectedChatId={(mode !== 'inline' || inlineModeOverridden) ? selectedChatId : undefined}
              onChatChange={(mode !== 'inline' || inlineModeOverridden) ? handleChatChange : undefined}
            />
          )}
        </div>

        {/* Roles Panel — always shown, expanded by default */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '16px',
          boxShadow: '0 1px 12px rgba(0,0,0,0.07)',
        }}>
          <div style={{ marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>Назначенные роли</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
              Предоставьте вашим коллегам возможность вести учет посещаемости в вашем событии.
            </p>
          </div>

          {submitted && createdEventId ? (
            // После сохранения — показываем настоящую панель с реальным event
            <RolesPanel
              event={{ id: createdEventId, organizationId, assignedRoles: pendingRoles }}
              createMode={false}
              userId={userId}
              hash={hash}
              autoOpenInviteModal={autoOpenInviteModal}
              onReturnToChat={inlinePendingPublicId
                ? () => window.Telegram?.WebApp?.switchInlineQuery(`event_${inlinePendingPublicId}`)
                : undefined}
            />
          ) : (
            <RolesPanel
              event={null}
              createMode={true}
              organizationId={organizationId}
              userId={userId}
              hash={hash}
              onPendingRolesChange={setPendingRoles}
              onSaveFirst={handleSaveFirst}
            />
          )}

          {/* Кнопка возврата в чат — только в чистом inline-режиме после saveFirst.
              Пользователь уже скопировал invite-ссылку, теперь может опубликовать событие в чат. */}
          {inlinePendingPublicId && (
            <button
              type="button"
              onClick={() => window.Telegram?.WebApp?.switchInlineQuery(`event_${inlinePendingPublicId}`)}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              Вернуться в чат для публикации
            </button>
          )}
        </div>
      </div>

      {/* Предупреждение: список событий недоступен в inline-режиме */}
      {showInlineWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 20px',
            width: '100%', maxWidth: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#dbeafe',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
              Список событий недоступен
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
              Для отображения и управления списком событий запустите бота из личного чата либо из опубликованного сообщения.
            </p>
            <button
              onClick={() => setShowInlineWarning(false)}
              style={{
                width: '100%', padding: '11px 16px',
                borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#fff', fontSize: 14, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Понятно
            </button>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
};

export default CreateEventPage;